import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, DatePicker, Button, Space, Typography, message, List, Progress, Tag, Spin, Statistic } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, RightOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AppDispatch, CombinedStates } from '../../core/types';
import { setSelectedSchool } from '../../core/bvm/bvmSlice';
import { getBvmSurveyStatistics, getBvmCategoryData, exportBvmSurveys } from '../../core/bvm/bvmApi';
import SchoolBvmCategoryData from '../SchoolBvmCategoryData';
import { BvmSurveySchool } from '../../core/bvm/types';
import './style.scss';

const { Title, Text } = Typography;

const EducationCenterBvmSurveys: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

    const {
        surveyStatistics,
        surveySchools,
        selectedSchool,
        singleCategory,
        isFetching,
        isExporting,
        error,
    } = useSelector((state: CombinedStates) => state.bvm);

    const handleDateChange = (date: dayjs.Dayjs | null) => {
        setSelectedDate(date);
    };

    const handleUpdate = async () => {
        if (!selectedDate) {
            message.warning('Prašome pasirinkti datą');
            return;
        }

        dispatch(getBvmSurveyStatistics({ date: selectedDate.format('YYYY-MM-DD') }));
    };

    const handleSchoolSelect = (school: BvmSurveySchool) => {
        dispatch(setSelectedSchool(school));
    };

    const handleCategoryClick = (categoryId: number) => {
        if (selectedSchool && selectedDate) {
            dispatch(getBvmCategoryData({
                school_id: selectedSchool.id,
                category_id: categoryId,
                date: selectedDate.format('YYYY-MM-DD'),
            }));
        }
    };

    const handleExport = async () => {
        if (!selectedSchool || !selectedDate) {
            message.warning('Prašome pasirinkti mokyklą ir datą');
            return;
        }

        try {
            const response = await dispatch(exportBvmSurveys({
                date: selectedDate.format('YYYY-MM-DD'),
                school_id: selectedSchool.id,
            })).unwrap();

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `bvm_apklausos_${selectedSchool.name.replace(/\s+/g, '_')}_${selectedDate.format('YYYY-MM-DD')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success('Apklausos sėkmingai eksportuotos');
        } catch (exportError) {
            console.error('Export failed:', exportError);
            message.error('Nepavyko eksportuoti apklausų');
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
        case 'completed':
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        case 'partially_completed':
            return <ClockCircleOutlined style={{ color: '#faad14' }} />;
        case 'not_completed':
            return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
        default:
            return null;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
        case 'completed':
            return 'success';
        case 'partially_completed':
            return 'warning';
        case 'not_completed':
            return 'error';
        default:
            return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
        case 'completed':
            return 'Užbaigta';
        case 'partially_completed':
            return 'Daliniai duomenys';
        case 'not_completed':
            return 'Neužpildyta';
        default:
            return status;
        }
    };

    if (error) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Text type="danger">Įvyko klaida kraunant apklausų rezultatus</Text>
                </div>
            </Card>
        );
    }

    return (
        <div className="education-center-bvm-surveys">
            <Card>
                <div className="survey-header">
                    <Title level={3}>BVM Apklausų rezultatai</Title>
                    <Text type="secondary">
                        Pasirinkite datą ir atnaujinkite apklausų rezultatus
                    </Text>
                </div>

                <div className="controls-section">
                    <Row gutter={16} align="middle">
                        <Col span={12}>
                            <div className="date-picker-container">
                                <Text strong style={{ marginBottom: 8, display: 'block' }}>
                                    Data:
                                </Text>
                                <DatePicker
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    format="YYYY-MM-DD"
                                    placeholder="Pasirinkite datą"
                                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                                    style={{ width: '100%' }} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="button-container">
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<ReloadOutlined />}
                                        onClick={handleUpdate}
                                        loading={isFetching}
                                        disabled={!selectedDate}>
                                        Atnaujinti duomenis
                                    </Button>
                                </Space>
                            </div>
                        </Col>
                    </Row>
                </div>

                {surveyStatistics && surveyStatistics.total_schools !== undefined && (
                    <div className="statistics-section">
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic
                                    title="Iš viso mokyklų"
                                    value={surveyStatistics.total_schools}
                                    prefix={<CheckCircleOutlined />} />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Užbaigta"
                                    value={surveyStatistics.completed_schools}
                                    valueStyle={{ color: '#52c41a' }}
                                    prefix={<CheckCircleOutlined />} />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Daliniai duomenys"
                                    value={surveyStatistics.partially_completed_schools}
                                    valueStyle={{ color: '#faad14' }}
                                    prefix={<ClockCircleOutlined />} />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="Neužpildyta"
                                    value={surveyStatistics.not_completed_schools}
                                    valueStyle={{ color: '#ff4d4f' }}
                                    prefix={<CloseCircleOutlined />} />
                            </Col>
                        </Row>
                        <Row style={{ marginTop: 16 }}>
                            <Col span={24}>
                                <div className="completion-progress">
                                    <Text strong>Užbaigimo procentas: {surveyStatistics.completion_percentage.toFixed(1)}%</Text>
                                    <Progress
                                        percent={surveyStatistics.completion_percentage}
                                        strokeColor={{
                                            '0%': '#ff4d4f',
                                            '50%': '#faad14',
                                            '100%': '#52c41a',
                                        }}
                                        style={{ marginTop: 8 }} />
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}

                {isFetching && (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Text>Kraunami BVM apklausų rezultatai...</Text>
                        </div>
                    </div>
                )}

                {!isFetching && singleCategory && (
                    <SchoolBvmCategoryData />
                )}

                {!isFetching && !singleCategory && surveySchools && surveySchools.length > 0 && (
                    <div className="main-content-section">
                        <Row gutter={16}>
                            <Col span={7} xs={24} sm={10} md={10} lg={7} xl={7}>
                                <div className="schools-section">
                                    <Title level={4}>Mokyklų sąrašas</Title>
                                    <Row gutter={[8, 8]}>
                                        {surveySchools.map((school) => (
                                            <Col span={24} key={school.id}>
                                                <Card
                                                    className={`school-item ${selectedSchool && selectedSchool.id === school.id ? 'selected' : ''}`}
                                                    onClick={() => handleSchoolSelect(school)}
                                                    style={{ cursor: 'pointer' }}
                                                    size="small">
                                                    <Row align="middle" gutter={12}>
                                                        <Col flex="none">
                                                            {getStatusIcon(school.completion_status)}
                                                        </Col>
                                                        <Col flex="auto">
                                                            <div className="school-info">
                                                                <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                                                                    {school.name}
                                                                </Text>
                                                                <Space wrap>
                                                                    <Tag color={getStatusColor(school.completion_status)}>
                                                                        {getStatusText(school.completion_status)}
                                                                    </Tag>
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                        {school.completed_categories}/{school.total_categories} kategorijos
                                                                    </Text>
                                                                </Space>
                                                            </div>
                                                        </Col>
                                                        <Col flex="none">
                                                            <Progress
                                                                percent={school.completion_percentage}
                                                                size="small"
                                                                style={{ width: 80 }} />
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </Col>
                            <Col span={17} xs={24} sm={14} md={14} lg={17} xl={17}>
                                {selectedSchool && selectedSchool.categories ? (
                                    <div className="school-details-section">
                                        <div className="school-details-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <Title level={4}>{selectedSchool.name}</Title>
                                                <Space>
                                                    <Tag color={getStatusColor(selectedSchool.completion_status)}>
                                                        {getStatusText(selectedSchool.completion_status)}
                                                    </Tag>
                                                    <Text type="secondary">
                                                        {selectedSchool.completed_categories}/{selectedSchool.total_categories} kategorijos užbaigtos
                                                    </Text>
                                                </Space>
                                            </div>
                                            <Button
                                                type="default"
                                                icon={<FileExcelOutlined />}
                                                onClick={handleExport}
                                                loading={isExporting}
                                                disabled={!selectedDate}>
                                                Eksportuoti į Excel
                                            </Button>
                                        </div>

                                        <div className="categories-section">
                                            <Title level={5}>Apklausų kategorijos</Title>
                                            <List
                                                dataSource={selectedSchool.categories}
                                                renderItem={(category) => (
                                                    <List.Item
                                                        className="category-item"
                                                        onClick={() => handleCategoryClick(category.id)}
                                                        style={{ cursor: 'pointer' }}
                                                        actions={[
                                                            <RightOutlined key="arrow" style={{ color: '#fdab01', fontSize: '16px' }} />,
                                                        ]}>
                                                        <List.Item.Meta
                                                            avatar={category.is_filled
                                                                ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                                : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                                            title={category.title}
                                                            description={category.submitted_at
                                                                ? `Pateikta: ${dayjs(category.submitted_at).format('YYYY-MM-DD HH:mm')}`
                                                                : 'Nepateikta'} />
                                                    </List.Item>
                                                )} />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="school-selection-placeholder">
                                        <Card>
                                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                                <Text type="secondary">
                                                    Pasirinkite mokyklą iš sąrašo norėdami peržiūrėti detales
                                                </Text>
                                            </div>
                                        </Card>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                )}

                {!isFetching && !singleCategory && (!surveySchools || surveySchools.length === 0) && (
                    <div className="content-placeholder">
                        <Card>
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <Text type="secondary">
                                    Pasirinkite datą ir spauskite &quot;Atnaujinti duomenis&quot; norėdami peržiūrėti apklausų rezultatus
                                </Text>
                            </div>
                        </Card>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default EducationCenterBvmSurveys;
